// Admin Pricing Plan service.
// Endpoints:
//  GET    /api/admin/pricing
//  GET    /api/admin/pricing/:id
//  POST   /api/admin/pricing
//  PUT    /api/admin/pricing/:id
//  DELETE /api/admin/pricing/:id
//  POST   /api/admin/pricing/reorder   (body { orderedIds: number[] })
//  GET    /api/pricing                 (public, no auth)

import { API_BASE_URL } from './apiConfig';

const ADMIN_PRICING_BASE = `${API_BASE_URL}/admin/pricing`;
const PUBLIC_PRICING_BASE = `${API_BASE_URL}/pricing`;

export interface PricingPlan {
  id: number;
  name: string;
  tagline: string | null;
  price: string;
  period: string | null;
  badge: string | null;
  cta_text: string;
  // DB-backed plans don't have this column -- it's only ever set on the
  // landing page's synthetic Enterprise/"extra" plan (see LandingPageSimple),
  // so real plans fall back to the pricing section's shared ctaLink.
  cta_link?: string;
  features: string[];
  display_order?: number;
  is_active?: boolean | number;
  created_at?: string;
  updated_at?: string;
}

export interface PricingPlanInput {
  name: string;
  tagline?: string | null;
  price: string;
  period?: string | null;
  badge?: string | null;
  cta_text?: string;
  features: string[];
  display_order?: number;
  is_active?: boolean;
}

function getAuthHeaders(): HeadersInit {
  const headers: HeadersInit = { 'Content-Type': 'application/json' };
  let adminToken: string | null = null;
  try {
    adminToken = sessionStorage.getItem('adminToken');
    if (!adminToken) {
      for (let i = 0; i < sessionStorage.length; i++) {
        const k = sessionStorage.key(i) || '';
        if (k.startsWith('admin_token')) {
          adminToken = sessionStorage.getItem(k);
          break;
        }
      }
    }
  } catch {
    // ignore
  }
  const authToken = adminToken || localStorage.getItem('authToken');
  if (authToken) headers['Authorization'] = `Bearer ${authToken}`;
  return headers;
}

async function parseResponse<T>(res: Response): Promise<T> {
  const body = await res.json().catch(() => null);
  if (!res.ok || !body || body.success === false) {
    throw new Error(body?.message || `Request failed (${res.status})`);
  }
  return body.data as T;
}

class AdminPricingService {
  async getAll(): Promise<PricingPlan[]> {
    const res = await fetch(ADMIN_PRICING_BASE, { headers: getAuthHeaders() });
    return parseResponse<PricingPlan[]>(res);
  }

  async getById(id: number): Promise<PricingPlan> {
    const res = await fetch(`${ADMIN_PRICING_BASE}/${id}`, { headers: getAuthHeaders() });
    return parseResponse<PricingPlan>(res);
  }

  async create(input: PricingPlanInput): Promise<PricingPlan> {
    const res = await fetch(ADMIN_PRICING_BASE, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(input),
    });
    return parseResponse<PricingPlan>(res);
  }

  async update(id: number, input: Partial<PricingPlanInput>): Promise<PricingPlan> {
    const res = await fetch(`${ADMIN_PRICING_BASE}/${id}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(input),
    });
    return parseResponse<PricingPlan>(res);
  }

  async remove(id: number): Promise<void> {
    const res = await fetch(`${ADMIN_PRICING_BASE}/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    await parseResponse<void>(res);
  }

  async reorder(orderedIds: number[]): Promise<PricingPlan[]> {
    const res = await fetch(`${ADMIN_PRICING_BASE}/reorder`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ orderedIds }),
    });
    return parseResponse<PricingPlan[]>(res);
  }

  // Public, unauthenticated -- used by the landing page
  async getPublicPlans(): Promise<PricingPlan[]> {
    const res = await fetch(PUBLIC_PRICING_BASE);
    return parseResponse<PricingPlan[]>(res);
  }
}

export default new AdminPricingService();
